import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MedicareApiPlan } from 'types/api';
import { DEFAULT_PLAN_YEAR } from 'lib/planYears';
import { isSamePlan, planIdentityKey } from 'utils/selectedPlans';

const MAX_PLANS = 2;

const comparisonYear = (plan: Pick<MedicareApiPlan, 'planYear'>) =>
  plan.planYear ?? DEFAULT_PLAN_YEAR;

const sameYearCount = (
  bucket: MedicareApiPlan[],
  plan: Pick<MedicareApiPlan, 'planYear'>
) => bucket.filter((p) => comparisonYear(p) === comparisonYear(plan)).length;

interface ComparisonState {
  plansByType: Record<string, MedicareApiPlan[]>;
  removingIds: string[];
}

const initialState: ComparisonState = {
  plansByType: {},
  removingIds: [],
};

const comparisonSlice = createSlice({
  name: 'comparison',
  initialState,
  reducers: {
    toggleCompare(
      state,
      action: PayloadAction<{ plan: MedicareApiPlan; planType: string }>
    ) {
      const { plan, planType } = action.payload;
      const bucket = state.plansByType[planType] ?? [];
      const idx = bucket.findIndex((p) => isSamePlan(p, plan));
      if (idx >= 0) {
        // Mark for removal so the tray can play the animation before removing
        const identityKey = planIdentityKey(plan);
        if (!state.removingIds.includes(identityKey)) {
          state.removingIds.push(identityKey);
        }
      } else if (sameYearCount(bucket, plan) < MAX_PLANS) {
        bucket.push(plan);
        state.plansByType[planType] = bucket;
      }
    },
    markForRemoval(
      state,
      action: PayloadAction<Pick<MedicareApiPlan, 'id' | 'planYear'>>
    ) {
      const identityKey = planIdentityKey(action.payload);
      if (!state.removingIds.includes(identityKey)) {
        state.removingIds.push(identityKey);
      }
    },
    seedComparison(
      state,
      action: PayloadAction<{ plan: MedicareApiPlan; planType: string }>
    ) {
      const { plan, planType } = action.payload;
      const bucket = state.plansByType[planType] ?? [];
      if (
        !bucket.some((p) => isSamePlan(p, plan)) &&
        sameYearCount(bucket, plan) < MAX_PLANS
      ) {
        state.plansByType[planType] = [...bucket, plan];
      }
    },
    removeFromComparison(
      state,
      action: PayloadAction<{
        plan: Pick<MedicareApiPlan, 'id' | 'planYear'>;
        planType: string;
      }>
    ) {
      const { plan, planType } = action.payload;
      const identityKey = planIdentityKey(plan);
      const bucket = state.plansByType[planType] ?? [];
      state.plansByType[planType] = bucket.filter((p) => !isSamePlan(p, plan));
      state.removingIds = state.removingIds.filter(
        (rid) => rid !== identityKey
      );
    },
    clearComparison(state) {
      state.plansByType = {};
    },
  },
});

export const {
  toggleCompare,
  markForRemoval,
  seedComparison,
  removeFromComparison,
  clearComparison,
} = comparisonSlice.actions;

export default comparisonSlice.reducer;
